import React from "react"
import {
	Box,
	Drawer,
	IconButton,
	ListItem,
	List,
	ListItemText,
	ListItemIcon,
	Tabs,
	Tab,
} from "@mui/material"
import { makeStyles } from "makeStyles"

import Icon from "@mdi/react"
import {
	mdiChevronLeft,
	mdiArrowLeftThick,
	mdiTableEdit,
	mdiPlaylistCheck,
	mdiHelpCircle,
	//mdiPlay,
	mdiEmailOutline,
} from "@mdi/js"

import Link from "components/i18n/Link"
import Button from "components/Buttons/Button"

import Logo from "svg/ds-logo-fff.svg"

import { LockingSystemConfiguratorView } from "../LockingSystemConfigurator"
import { LockingSystemMatrixType } from "@secureo/common/typings/LockingSystem"

import { Email, Phone } from "components/i18n/Contact"
import { CountryCode } from "@secureo/common/typings/CountryCode"

export const sidebarWidthPx = 280

const useStyles = makeStyles()((theme: any) => ({
	root: {
		display: "flex",
	},
	appBar: {
		background: "#263739",
		transition: theme.transitions.create(["margin", "width"], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
	},
	appBarShift: {
		width: `calc(100% - ${sidebarWidthPx}px)`,
		marginLeft: sidebarWidthPx,
		transition: theme.transitions.create(["margin", "width"], {
			easing: theme.transitions.easing.easeOut,
			duration: theme.transitions.duration.enteringScreen,
		}),
	},
	drawer: {
		width: sidebarWidthPx,
		flexShrink: 0,
	},
	drawerPaper: {
		width: sidebarWidthPx,
		paddingLeft: 10,
		background:
			"linear-gradient(61deg, rgba(67,138,196,1) 41%, rgba(68,141,200,1) 41%, rgba(68,141,200,1) 55%, rgba(70,145,206,1) 55%);",
		color: "white",
	},
	drawerHeader: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(0, 1),
		// necessary for content to be below app bar
		...theme.mixins.toolbar,
		justifyContent: "flex-end",
	},
	content: {
		flexGrow: 1,
		padding: theme.spacing(3),
		transition: theme.transitions.create("margin", {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
		marginLeft: -sidebarWidthPx,
	},
	contentShift: {
		transition: theme.transitions.create("margin", {
			easing: theme.transitions.easing.easeOut,
			duration: theme.transitions.duration.enteringScreen,
		}),
		marginLeft: 0,
	},
}))

const LockingSystemSidebar = ({
	sidebarIsOpen,
	toggleSidebarIsOpen,
	lockingSystemCategorySlug,
	view,
	setView,
	startTourGuide,
	matrixType,
	countryCode,
}: Props) => {
	const { classes } = useStyles()

	const setOverViewView = () => {
		if (matrixType !== null) {
			setView("overview")
		} else {
			alert("Bitte konfigurieren Sie erst Ihre Berechtigungen")
		}
	}

	return (
		<Drawer
			className={classes.drawer}
			variant="persistent"
			anchor="left"
			open={sidebarIsOpen}
			classes={{
				paper: classes.drawerPaper,
			}}
		>
			<div
				className={classes.drawerHeader}
				style={{ display: "flex", justifyContent: "space-between" }}
			>
				<div>
					<Logo
						style={{
							width: 160,
							height: 100,
							marginLeft: 15,
						}}
					/>
				</div>
				<IconButton onClick={toggleSidebarIsOpen} size="large">
					<Icon path={mdiChevronLeft} size={1} color="white" />
				</IconButton>
			</div>

			<Box mt={3}>
				<ListItem>
					<ListItemText primary="Bearbeiten" style={{ textTransform: "uppercase" }} />
				</ListItem>
				<Tabs
					value={view === "matrix" ? 0 : 1}
					aria-label="simple tabs example"
					orientation="vertical"
					indicatorColor="secondary"
				>
					<Tab
						disableRipple
						style={{ textTransform: "inherit", marginLeft: -10 }}
						label={
							<ListItem button style={{ color: "#fff" }}>
								<ListItemIcon onClick={() => setView("matrix")}>
									<Icon path={mdiTableEdit} size={1} color="white" />
								</ListItemIcon>
								<ListItemText
									primary="Schließplan"
									onClick={() => setView("matrix")}
								/>
							</ListItem>
						}
					/>
					<Tab
						disableRipple
						style={{ textTransform: "inherit", marginLeft: -10, marginTop: -15 }}
						label={
							<ListItem button style={{ color: "#fff" }}>
								<ListItemIcon onClick={setOverViewView}>
									<Icon path={mdiPlaylistCheck} size={1} color="white" />
								</ListItemIcon>
								<ListItemText primary="Konfigurationen" onClick={setOverViewView} />
							</ListItem>
						}
					/>
				</Tabs>
			</Box>

			<Box mt={5}>
				<List>
					<ListItem>
						<ListItemText primary="Hilfe" style={{ textTransform: "uppercase" }} />
					</ListItem>
					<ListItem button>
						<ListItemIcon onClick={startTourGuide}>
							<Icon path={mdiHelpCircle} size={1} color="white" />
						</ListItemIcon>
						<ListItemText primary="Erste Schritte" onClick={startTourGuide} />
					</ListItem>
					{/* <ListItem button>
						<ListItemIcon>
							<Icon path={mdiPlay} size={1} color="white" />
						</ListItemIcon>
						<ListItemText primary="Videoerklärung" />
					</ListItem> */}
				</List>
			</Box>
			<Box mt={5}>
				<ListItem>
					<ListItemText primary="Kontakt" style={{ textTransform: "uppercase" }} />
				</ListItem>
				<ListItem>
					<ListItemText primary={<Phone countryCode={countryCode} />} />
				</ListItem>
				<ListItem>
					<ListItemText
						primary={
							<div style={{ display: "flex", alignItems: "center" }}>
								<Icon
									path={mdiEmailOutline}
									size={0.7}
									style={{ marginRight: 10 }}
								/>
								<Email />
							</div>
						}
					/>
				</ListItem>
			</Box>

			<div style={{ position: "absolute", bottom: 30, left: 45 }}>
				<Link href={`/locking-system/${lockingSystemCategorySlug}`}>
					<Button color="link-no-underline">
						<Icon path={mdiArrowLeftThick} size={1} color="white" />
						<Box ml={1} style={{ color: "white" }}>
							<strong>Zurück zum System</strong>
						</Box>
					</Button>
				</Link>
			</div>
		</Drawer>
	)
}

interface Props {
	sidebarIsOpen: boolean
	toggleSidebarIsOpen: () => void
	view: LockingSystemConfiguratorView
	setView: (view: LockingSystemConfiguratorView) => void
	lockingSystemCategorySlug?: string
	startTourGuide: () => void
	matrixType: LockingSystemMatrixType
	countryCode: CountryCode
}

export default LockingSystemSidebar
